import { useState } from 'react';
import { Category } from '../types';
import { CategoryList } from './CategoryList';
import { CategoryForm } from './CategoryForm';
import { useCategories } from '../hooks/useCategories';
import { Portal } from './Portal';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CategoryModal({ isOpen, onClose }: CategoryModalProps) {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | undefined>();

  const pageSize = 5; // Menor para o modal

  const {
    categories,
    total,
    loading,
    error,
    refresh,
    deleteCategory
  } = useCategories({
    search: search || undefined,
    page: currentPage,
    pageSize,
  });

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingCategory(undefined);
    refresh();
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingCategory(undefined);
  };

  const handleNewCategory = () => {
    setEditingCategory(undefined);
    setIsFormOpen(true);
  };

  if (!isOpen) return null;

  if (error) {
    return (
      <div className="fixed inset-0 bg-[#0f172a]/80 flex items-center justify-center p-8 z-[60] backdrop-blur-md">
        <div className="flex flex-col items-center gap-6 bg-[#1e293b]/90 p-12 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center border border-red-500/20 mb-2">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight">Erro ao carregar</h2>
          <p className="text-gray-400 text-sm font-medium leading-relaxed">{error}</p>
          <div className="flex flex-col w-full gap-3">
            <button
              onClick={refresh}
              className="w-full px-8 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 border border-blue-500 relative overflow-hidden group/btn font-oswald"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              Tentar novamente
            </button>
            <button
              onClick={onClose}
              className="w-full px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border border-white/10 backdrop-blur-md">
        {/* Header */}
        <div className="bg-white/5 p-10 border-b border-white/10 backdrop-blur-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-blue-900/20 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-inner">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">Gerenciar Categorias</h2>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald mt-2">Organize seus contatos de forma inteligente</p>
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleNewCategory}
                className="px-8 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:scale-105 transition-all duration-300 flex items-center gap-3 border border-blue-500 relative overflow-hidden group/btn font-oswald"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nova Categoria
              </button>
              <button
                onClick={onClose}
                className="p-4 text-gray-400 hover:text-white hover:bg-white/10 rounded-2xl transition-all border border-white/5 hover:border-white/10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="px-10 py-6 border-b border-white/10 bg-white/5">
          <div className="flex justify-between items-center gap-6">
            <div className="flex-1 relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-500 group-focus-within:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="BUSCAR CATEGORIAS..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all font-oswald font-bold text-[10px] tracking-[0.2em]"
              />
            </div>
            {search && (
              <button
                onClick={handleClearFilters}
                className="px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl hover:bg-white/10 transition-all border border-white/5 hover:border-white/10 font-oswald"
              >
                Limpar Filtros
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-0 custom-scrollbar">
          <CategoryList
            categories={categories}
            loading={loading}
            onEdit={handleEdit}
            onDelete={deleteCategory}
          />
        </div>

        {/* Pagination */}
        {total > pageSize && (
          <div className="px-10 py-8 border-t border-white/10 bg-white/5 backdrop-blur-md">
            <div className="flex justify-between items-center">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.2em] font-oswald">
                Mostrando {categories.length} de {total} categorias
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-6 py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl border border-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 transition-all font-oswald"
                >
                  Anterior
                </button>

                {Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-10 h-10 rounded-xl font-bold text-[10px] transition-all font-oswald ${
                      currentPage === page
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40 border border-blue-500'
                        : 'bg-white/5 text-gray-500 border border-white/5 hover:bg-white/10'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === Math.ceil(total / pageSize)}
                  className="px-6 py-3 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl border border-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 transition-all font-oswald"
                >
                  Próxima
                </button>
              </div>
            </div>
            <div className="text-center text-sm text-gray-400 mt-3">
              Mostrando {categories.length} de {total} categorias (Página {currentPage} de {Math.ceil(total / pageSize)})
            </div>
          </div>
        )}

        {/* Category Form Modal */}
        {isFormOpen && (
          <Portal>
            <CategoryForm
              category={editingCategory}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </Portal>
        )}
      </div>
    </div>
  );
}